import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import QuickActions from './QuickActions';

const Dashboard = ({ apiBase, user }) => {
  const navigate = useNavigate();
  const [developerData, setDeveloperData] = useState(null);
  const [sirData, setSirData] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [mediaStats, setMediaStats] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [droneShoots, setDroneShoots] = useState(null);
  const [whatsappIntimations, setWhatsappIntimations] = useState([]);
  const [ethnicityStats, setEthnicityStats] = useState([]);
  const [propertySellingStats, setPropertySellingStats] = useState(null);
  const [marketAnalysis, setMarketAnalysis] = useState(null);
  const [bhkConfigs, setBhkConfigs] = useState([]);
  const [bhkStats, setBhkStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllDashboardData();
  }, []);

  const fetchAllDashboardData = async () => {
    try {
      const [
        devRes,
        sirRes,
        salesStatsRes,
        clientStatsRes,
        mediaStatsRes,
        funnelRes,
        droneShootsRes,
        whatsappIntimationsRes,
        ethnicityStatsRes,
        propertySellingRes,
        marketAnalysisRes,
        bhkConfigsRes,
        bhkStatsRes
      ] = await Promise.all([
        axios.get(`${apiBase}/dashboard/developer`),
        axios.get(`${apiBase}/dashboard/sir`),
        axios.get(`${apiBase}/sales/stats/summary`),
        axios.get(`${apiBase}/clients/stats/summary`),
        axios.get(`${apiBase}/media/stats/summary`),
        axios.get(`${apiBase}/reports/conversion-funnel`),
        Promise.resolve({ data: null }), // Pixeldo removed 2026-09-23
        Promise.resolve({ data: [] }), // WhatsApp intimation removed 2026-09-23
        axios.get(`${apiBase}/vdnb/ethnicity-stats`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/vdnb/properties-to-sell/all`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/market-analysis`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/bhk-config`).catch(() => ({ data: [] })),
        axios.get(`${apiBase}/bhk-config/stats/summary`).catch(() => ({ data: null }))
      ]);
      setDeveloperData(devRes.data);
      setSirData(sirRes.data);
      setSalesStats(salesStatsRes.data);
      setClientStats(clientStatsRes.data);
      setMediaStats(mediaStatsRes.data);
      setFunnelData(funnelRes.data);
      setDroneShoots(droneShootsRes.data);
      setWhatsappIntimations(whatsappIntimationsRes.data || []);
      setEthnicityStats(ethnicityStatsRes.data || []);
      setBhkConfigs(bhkConfigsRes.data || []);
      setBhkStats(bhkStatsRes.data || null);

      // Get market analysis - simple approach
      try {
        const list = marketAnalysisRes.data || [];
        if (list.length > 0) {
          const full = await axios.get(`${apiBase}/market-analysis/${list[0].id}`);
          setMarketAnalysis(full.data);
        }
      } catch (err) {
        console.log('Market analysis not available');
      }

      // Calculate property selling stats
      const properties = propertySellingRes.data || [];
      setPropertySellingStats({
        total: properties.length,
        totalValue: properties.reduce((sum, p) => sum + (parseFloat(p.expected_price) || 0), 0),
        byType: properties.reduce((acc, p) => {
          const type = p.property_type || 'Unknown';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {}),
        byBHK: properties.reduce((acc, p) => {
          const bhk = p.bhk_type || 'Unknown';
          acc[bhk] = (acc[bhk] || 0) + 1;
          return acc;
        }, {})
      });

      // Extract strategy data from developer data
      if (devRes.data) {
        setStrategies(devRes.data.sales_strategies || []);
        setUpcomingActivities(devRes.data.upcoming_activities || []);
        setUpcomingMeetings(devRes.data.upcoming_meetings || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const errorMsg = error.message || 'Unknown error';
      if (errorMsg.includes('Network Error') || errorMsg.includes('ERR_CONNECTION_REFUSED')) {
        alert('Cannot connect to server. Please make sure the backend server is running on ');
      } else {
        alert(`Error loading dashboard: ${error.response?.data?.error || errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0', '#ffb347', '#87ceeb'];

  // SALES USER VIEW: "My Day" & Focused Tasks
  if (user?.role === 'sales') {
    return (
      <div className="dashboard-container">
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: 0 }}>Good Morning, {user.name.split(' ')[0]} ☀️</h1>
          <p style={{ color: '#64748b', marginTop: '4px' }}>Let's close some deals today!</p>
        </div>

        {/* Sales Stats Grid */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
          <div className="stat-card" style={{ background: '#eff6ff', border: '1px solid #dbeafe' }}>
            <div className="stat-label">My Leads</div>
            <div className="stat-value" style={{ color: '#2563eb' }}>{clientStats?.total?.total || 0}</div>
          </div>
          <div className="stat-card" style={{ background: '#f0fdf4', border: '1px solid #dcfce7' }}>
            <div className="stat-label">Site Visits</div>
            <div className="stat-value" style={{ color: '#10b981' }}>{developerData?.activities?.today_walkins || 0}</div>
            <div className="stat-label" style={{ fontSize: '11px' }}>Today</div>
          </div>
          <div className="stat-card" style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
            <div className="stat-label">Pending Follow-ups</div>
            <div className="stat-value" style={{ color: '#d97706' }}>{upcomingActivities.length}</div>
          </div>
        </div>

        {/* My Day / Tasks */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px', marginTop: '24px' }}>

          {/* Upcoming Activities */}
          <div className="card">
            <h2 className="card-title">📅 My Scheduled Tasks</h2>
            {upcomingActivities.length > 0 ? (
              <div className="activity-list">
                {upcomingActivities.slice(0, 5).map((activity, idx) => (
                  <div key={idx} className="activity-item" style={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{activity.type} with {activity.client_name}</div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>
                      {new Date(activity.date).toLocaleDateString()} at {activity.time || 'All Day'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>No pending tasks for today. Great job!</p>
            )}
            <div style={{ marginTop: '16px' }}>
              <Link to="/followups" className="btn btn-outline" style={{ width: '100%', justifyContent: 'center' }}>
                View All Tasks →
              </Link>
            </div>
          </div>

          {/* Quick Shortcuts */}
          <div className="card" style={{ background: '#f8fafc' }}>
            <h2 className="card-title">⚡ Quick Shortcuts</h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <Link to="/clients?action=new" className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }}>+ Add Lead</Link>
              <Link to="/walkins?action=new" className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>+ Log Visit</Link>
              <Link to="/inventory" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }}>Check Inventory</Link>
            </div>
          </div>

        </div>

        {/* Global Quick Actions FAB */}
        <QuickActions />
      </div>
    );
  }

  // ADMIN / MANAGER VIEW (Original Dashboard)
  return (
    <div>
      <h1 style={{ marginBottom: '24px' }}>Developer Dashboard</h1>

      {/* Main Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Sales</div>
          <div className="stat-value">{developerData?.sales_overview?.total_sales || 0}</div>
          <div className="stat-label">Revenue: ₹{formatCurrency(developerData?.sales_overview?.total_revenue || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Direct SIR Sales</div>
          <div className="stat-value">{developerData?.sales_overview?.direct_sir_sales || 0}</div>
          <div className="stat-label">Avg Value: ₹{formatCurrency(developerData?.sales_overview?.avg_sale_value || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Channel Partner Sales</div>
          <div className="stat-value">{developerData?.sales_overview?.channel_sales || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Commission Payable</div>
          <div className="stat-value">₹{formatCurrency(developerData?.commission_overview?.total_commission_payable || 0)}</div>
          <div className="stat-label">SIR: ₹{formatCurrency(developerData?.commission_overview?.total_sir_commission || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Clients</div>
          <div className="stat-value">{clientStats?.total?.total || 0}</div>
        </div>
        <Link
          to="/walkins"
          style={{ textDecoration: 'none', color: 'inherit' }}
          className="stat-card"
          title="Click to view detailed walk-ins management"
        >
          <div className="stat-label">Total Walk-ins</div>
          <div className="stat-value">{developerData?.activities?.total_walkins || 0}</div>
          <div className="stat-label">Today: {developerData?.activities?.today_walkins || 0} | This Week: {developerData?.activities?.week_walkins || 0}</div>
          {developerData?.walkins_conversion && (
            <div className="stat-label" style={{ fontSize: '11px', marginTop: '4px', color: '#10b981' }}>
              {developerData.walkins_conversion.converted_to_sale || 0} converted to sales
            </div>
          )}
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>View All Walk-ins →</div>
        </Link>
        <div className="stat-card">
          <div className="stat-label">META Leads</div>
          <div className="stat-value">{developerData?.meta_leads?.total_leads || 0}</div>
          <div className="stat-label">Converted: {developerData?.meta_leads?.converted_leads || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Brokers</div>
          <div className="stat-value">{developerData?.active_brokers?.length || 0}</div>
        </div>
        <Link
          to="/vdnb"
          style={{ textDecoration: 'none', color: 'inherit' }}
          className="stat-card"
          title="Click to view VDNB data"
        >
          <div className="stat-label">Clients Selling Property</div>
          <div className="stat-value" style={{ color: '#10b981' }}>{propertySellingStats?.total || 0}</div>
          <div className="stat-label">Total Value: ₹{formatCurrency(propertySellingStats?.totalValue || 0)}</div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>View VDNB →</div>
        </Link>
        <Link
          to="/intelligence"
          style={{ textDecoration: 'none', color: 'inherit' }}
          className="stat-card"
          title="Click to view The Intelligence - Market Analysis"
        >
          <div className="stat-label">🧠 The Intelligence</div>
          <div className="stat-value" style={{ color: '#1e40af', fontSize: '28px' }}>Market Insights</div>
          <div className="stat-label">Competition, Distances & Rates</div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#2563eb', fontWeight: '500' }}>View Intelligence →</div>
        </Link>
      </div>

      {/* BHK Configuration Section */}
      {bhkConfigs.length > 0 && (
        <div className="card" style={{ marginBottom: '24px', border: '2px solid #2563eb', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h2 className="card-title" style={{ color: '#1e40af', marginBottom: '8px' }}>🏠 BHK Configuration</h2>
              <p style={{ color: '#64748b', fontSize: '14px' }}>Towers - BHK and Area Configuration</p>
            </div>
            <Link
              to="/bhk"
              className="btn btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              View All Configurations →
            </Link>
          </div>

          {bhkStats && (
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
              <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
                <div className="stat-label">Total Configurations</div>
                <div className="stat-value" style={{ color: '#2563eb' }}>{bhkStats.total_configs || 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
                <div className="stat-label">Total Towers</div>
                <div className="stat-value" style={{ color: '#2563eb' }}>{bhkStats.total_towers || 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
                <div className="stat-label">BHK Types</div>
                <div className="stat-value" style={{ color: '#2563eb' }}>{bhkStats.total_bhk_types || 0}</div>
              </div>
              <div className="stat-card" style={{ background: 'white', border: '1px solid #bfdbfe' }}>
                <div className="stat-label">Active</div>
                <div className="stat-value" style={{ color: '#10b981' }}>{bhkStats.active_configs || 0}</div>
              </div>
            </div>
          )}

          <div className="table-container" style={{ background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
            <table className="table" style={{ margin: 0 }}>
              <thead style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', color: 'white' }}>
                <tr>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Tower</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>BHK Type</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Carpet Area</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Built-up Area</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Super Area</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Area 1</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Area 2</th>
                  <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Area 3</th>
                </tr>
              </thead>
              <tbody>
                {bhkConfigs.slice(0, 10).map((config, idx) => (
                  <tr key={config.id} style={{
                    background: idx % 2 === 0 ? '#f9fafb' : 'white',
                    transition: 'background 0.2s'
                  }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0f9ff'}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#f9fafb' : 'white'}>
                    <td style={{ padding: '12px' }}><strong style={{ color: '#1e40af' }}>{config.tower_name}</strong></td>
                    <td style={{ padding: '12px' }}><strong>{config.bhk_type}</strong></td>
                    <td style={{ padding: '12px' }}>{config.carpet_area ? parseFloat(config.carpet_area).toFixed(2) : '-'}</td>
                    <td style={{ padding: '12px' }}>{config.built_up_area ? parseFloat(config.built_up_area).toFixed(2) : '-'}</td>
                    <td style={{ padding: '12px' }}>{config.super_area ? parseFloat(config.super_area).toFixed(2) : '-'}</td>
                    <td style={{ padding: '12px' }}>{config.area_1 ? parseFloat(config.area_1).toFixed(2) : '-'}</td>
                    <td style={{ padding: '12px' }}>{config.area_2 ? parseFloat(config.area_2).toFixed(2) : '-'}</td>
                    <td style={{ padding: '12px' }}>{config.area_3 ? parseFloat(config.area_3).toFixed(2) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {bhkConfigs.length > 10 && (
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Link to="/bhk" style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}>
                View all {bhkConfigs.length} configurations →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Market Analysis - Simple Display */}
      {marketAnalysis && marketAnalysis.analysis ? (
        <div className="card" style={{
          marginBottom: '24px',
          border: '2px solid #10b981',
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h2 className="card-title" style={{ color: '#065f46', marginBottom: '8px', fontSize: '24px' }}>
                📊 Micro Market Analysis - {marketAnalysis.analysis.location}
              </h2>
              <p style={{ color: '#047857', fontSize: '14px' }}>
                Comprehensive market insights, competitor analysis & location advantages | Analysis Radius: {marketAnalysis.analysis.analysis_radius_km || 5} km
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{
                padding: '10px 20px',
                background: '#10b981',
                color: 'white',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '700',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                ⭐ {marketAnalysis.analysis.market_rating || 5}/5 Rating
              </div>
              <Link
                to={`/intelligence`}
                className="btn btn-primary"
                style={{
                  textDecoration: 'none',
                  background: 'white',
                  color: '#10b981',
                  border: '2px solid #10b981',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#10b981';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.color = '#10b981';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                }}
              >
                View Full Analysis →
              </Link>
            </div>
          </div>

          {/* Market Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Total Projects</div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                {marketAnalysis.analysis.total_projects || 0}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>by {marketAnalysis.analysis.total_builders || 0} builders</div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Sales Volume</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                ₹{formatCurrency((marketAnalysis.analysis.sales_volume_crores || 0) * 10000000)} Cr
              </div>
              <div style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>🏆 2024-25 Record</div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Price Appreciation</div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                {marketAnalysis.analysis.price_appreciation_3yr || 0}%
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>📈 3-year growth</div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Avg Price/sq.ft</div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                ₹{formatCurrency(marketAnalysis.analysis.avg_price_per_sqft || 0)}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>Market average</div>
            </div>
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              transition: 'transform 0.2s'
            }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Rental Yield</div>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#059669', marginBottom: '4px' }}>
                {marketAnalysis.analysis.rental_yield || 0}%
              </div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>💰 Annual yield</div>
            </div>
          </div>

          {/* Competitor Projects Comparison */}
          {marketAnalysis.competitors && marketAnalysis.competitors.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#065f46', marginBottom: '16px' }}>
                🏢 Competitor Projects Comparison ({marketAnalysis.competitors.length} projects)
              </h3>
              <div className="table-container" style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <table className="table" style={{ margin: 0 }}>
                  <thead style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}>
                    <tr>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Project</th>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Developer</th>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Config</th>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Price Range</th>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Price/sq.ft</th>
                      <th style={{ color: 'white', fontWeight: '600', padding: '12px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {marketAnalysis.competitors.slice(0, 7).map((project, idx) => (
                      <tr key={idx} style={{
                        background: idx % 2 === 0 ? '#f9fafb' : 'white',
                        transition: 'background 0.2s'
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f0fdf4'}
                        onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? '#f9fafb' : 'white'}>
                        <td style={{ padding: '12px' }}><strong style={{ color: '#065f46' }}>{project.project_name}</strong></td>
                        <td style={{ fontSize: '13px', color: '#64748b', padding: '12px' }}>{project.developer_name || '-'}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            background: '#d1fae5',
                            color: '#065f46',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {project.configuration || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {project.price_min && project.price_max ? (
                            <span style={{ fontWeight: '600', color: '#059669' }}>
                              ₹{formatCurrency(project.price_min)} - ₹{formatCurrency(project.price_max)}
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {project.price_per_sqft ? (
                            <span style={{ fontWeight: '600', color: '#10b981' }}>
                              ₹{formatCurrency(project.price_per_sqft)}/sq.ft
                            </span>
                          ) : '-'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            padding: '4px 8px',
                            background: project.possession_status === 'Ready' ? '#d1fae5' : '#fef3c7',
                            color: project.possession_status === 'Ready' ? '#065f46' : '#92400e',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {project.possession_status || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Key Amenities Grid */}
          {marketAnalysis.amenities && marketAnalysis.amenities.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#065f46', marginBottom: '16px' }}>
                📍 Key Amenities & Distances ({marketAnalysis.amenities.length} amenities)
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                {marketAnalysis.amenities.slice(0, 12).map((amenity, idx) => (
                  <div key={idx} style={{
                    padding: '16px',
                    background: 'white',
                    borderRadius: '12px',
                    border: '1px solid #a7f3d0',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
                          {amenity.amenity_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {amenity.amenity_type}
                        </div>
                      </div>
                      <div style={{
                        padding: '8px 14px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '700',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        {amenity.distance_km} km
                      </div>
                    </div>
                    {amenity.travel_time_minutes && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>⏱️</span> {amenity.travel_time_minutes} minutes
                      </div>
                    )}
                    {amenity.description && (
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                        {amenity.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Demographics & Market Insights */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
            {marketAnalysis.analysis.demographics_summary && (
              <div style={{
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #a7f3d0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#065f46', marginBottom: '12px' }}>
                  👥 Demographics
                </h3>
                <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8' }}>
                  {marketAnalysis.analysis.demographics_summary}
                </div>
              </div>
            )}
            {marketAnalysis.analysis.cbd_locations && (
              <div style={{
                padding: '20px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #a7f3d0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#065f46', marginBottom: '12px' }}>
                  🏢 CBD Locations (5 km)
                </h3>
                <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8' }}>
                  {marketAnalysis.analysis.cbd_locations}
                </div>
              </div>
            )}
          </div>

          {/* Market Insights */}
          {marketAnalysis.analysis.competition_analysis && (
            <div style={{
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #a7f3d0',
              marginBottom: '24px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#065f46', marginBottom: '12px' }}>
                💡 Market Insights & Competition Analysis
              </h3>
              <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.8' }}>
                {marketAnalysis.analysis.competition_analysis}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{
          marginBottom: '24px',
          border: '2px dashed #10b981',
          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
          padding: '40px',
          textAlign: 'center'
        }}>
          <h2 style={{ color: '#065f46', marginBottom: '12px' }}>📊 Market Analysis</h2>
          <p style={{ color: '#047857', fontSize: '14px' }}>
            <a href="/intelligence" style={{display:"inline-block",marginTop:"12px",background:"#2563eb",color:"white",padding:"10px 20px",borderRadius:"8px",textDecoration:"none",fontWeight:"600"}}>View Full Intelligence Report →</a>
          </p>
        </div>
      )}

      {/* Walk-ins Overview */}
      {developerData?.activities && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ marginBottom: 0 }}>🚶 Walk-ins Overview - Live Data</h2>
            <Link
              to="/walkins"
              className="btn btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              View All Walk-ins →
            </Link>
          </div>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Total Walk-ins</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{developerData.activities.total_walkins || 0}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Today</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{developerData.activities.today_walkins || 0}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>This Week</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{developerData.activities.week_walkins || 0}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>This Month</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{developerData.activities.month_walkins || 0}</div>
            </div>
          </div>

          {/* Walk-ins Conversion Stats */}
          {developerData?.walkins_conversion && (
            <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
              <div className="stat-card">
                <div className="stat-label">Converted to Clients</div>
                <div className="stat-value" style={{ color: '#10b981' }}>{developerData.walkins_conversion.converted_to_client || 0}</div>
                <div className="stat-label">Rate: {developerData.walkins_conversion.conversion_to_client_rate || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Converted to Sales</div>
                <div className="stat-value" style={{ color: '#2563eb' }}>{developerData.walkins_conversion.converted_to_sale || 0}</div>
                <div className="stat-label">Rate: {developerData.walkins_conversion.conversion_to_sale_rate || 0}%</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Walk-ins</div>
                <div className="stat-value">{developerData.walkins_conversion.total_walkins || 0}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Conversion Efficiency</div>
                <div className="stat-value" style={{ color: '#f59e0b' }}>
                  {developerData.walkins_conversion.total_walkins > 0
                    ? ((developerData.walkins_conversion.converted_to_sale / developerData.walkins_conversion.total_walkins) * 100).toFixed(1)
                    : 0}%
                </div>
                <div className="stat-label">Walk-in to Sale</div>
              </div>
            </div>
          )}

          {/* Walk-ins by Source */}
          {developerData?.walkins_by_source && developerData.walkins_by_source.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Walk-ins by Source</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Total</th>
                      <th>Today</th>
                      <th>This Week</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {developerData.walkins_by_source.map((source, idx) => {
                      const total = developerData.walkins_by_source.reduce((sum, s) => sum + (s.count || 0), 0);
                      const percentage = total > 0 ? ((source.count / total) * 100).toFixed(1) : 0;
                      return (
                        <tr key={idx}>
                          <td>
                            <span className="badge" style={{
                              backgroundColor: source.source === 'broker' ? '#3b82f6' :
                                source.source === 'walkin' ? '#10b981' :
                                  source.source === 'website' ? '#8b5cf6' : '#64748b',
                              color: '#fff'
                            }}>
                              {source.source || 'Unknown'}
                            </span>
                          </td>
                          <td><strong>{source.count || 0}</strong></td>
                          <td>{source.today_count || 0}</td>
                          <td>{source.week_count || 0}</td>
                          <td>{percentage}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Walk-ins Trend Chart */}
          {developerData?.walkins_by_date && developerData.walkins_by_date.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Walk-ins Trend (Last 30 Days)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={developerData.walkins_by_date}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#667eea" name="Total Walk-ins" />
                  <Bar dataKey="broker_walkins" fill="#3b82f6" name="Broker Walk-ins" />
                  <Bar dataKey="direct_walkins" fill="#10b981" name="Direct Walk-ins" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Recent Walk-ins Table */}
          {developerData?.recent_walkins && developerData.recent_walkins.length > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', margin: 0 }}>Recent Walk-ins (Last 20)</h3>
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                    Click any row to view full walk-ins management page
                  </p>
                </div>
                <Link
                  to="/walkins"
                  className="btn btn-primary"
                  style={{
                    textDecoration: 'none',
                    fontSize: '14px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  View All Walk-ins →
                </Link>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Source</th>
                      <th>Broker</th>
                      <th>Project</th>
                      <th>Visit Date</th>
                      <th>Assigned To</th>
                      <th>Client Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {developerData.recent_walkins.map((walkin, idx) => (
                      <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => navigate('/walkins')}>
                        <td><strong>{walkin.name}</strong></td>
                        <td>{walkin.phone}</td>
                        <td>{walkin.email || '-'}</td>
                        <td>
                          <span className={`badge ${walkin.source === 'broker' ? 'badge-info' : 'badge-success'}`}>
                            {walkin.source || 'walkin'}
                          </span>
                        </td>
                        <td>{walkin.broker_name ? (
                          <span style={{ fontSize: '12px' }}>{walkin.broker_name}</span>
                        ) : '-'}</td>
                        <td>{walkin.project_name || '-'}</td>
                        <td>{new Date(walkin.visit_date).toLocaleDateString()}</td>
                        <td>{walkin.assigned_to_name || '-'}</td>
                        <td>
                          {walkin.client_status ? (
                            <span className={`badge badge-${walkin.client_status === 'booked' ? 'success' : walkin.client_status === 'lead' ? 'warning' : 'info'}`}>
                              {walkin.client_status}
                            </span>
                          ) : (
                            <span className="badge badge-secondary">Not Converted</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sales Velocity Chart */}
      {developerData?.sales_velocity && developerData.sales_velocity.length > 0 && (
        <div className="card">
          <h2 className="card-title">Sales Velocity (Last 30 Days)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={developerData.sales_velocity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="sales_count" fill="#2563eb" name="Sales Count" />
              <Bar dataKey="daily_revenue" fill="#10b981" name="Daily Revenue (₹)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Monthly Sales Trend */}
      {developerData?.monthly_trends && developerData.monthly_trends.length > 0 && (
        <div className="card">
          <h2 className="card-title">Monthly Sales Trend</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={developerData.monthly_trends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#2563eb" name="Revenue (₹)" />
              <Line type="monotone" dataKey="sales_count" stroke="#10b981" name="Sales Count" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Conversion Funnel */}
      {funnelData && (
        <div className="card">
          <h2 className="card-title">Conversion Funnel</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={[
              { name: 'Leads', value: funnelData.leads },
              { name: 'Interested', value: funnelData.interested },
              { name: 'Inspection', value: funnelData.inspection },
              { name: 'Booked', value: funnelData.booked },
              { name: 'Sold', value: funnelData.sold }
            ]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#2563eb" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Client Pipeline */}
      {developerData?.client_pipeline && developerData.client_pipeline.length > 0 && (
        <div className="card">
          <h2 className="card-title">Client Pipeline by Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={developerData.client_pipeline}
                dataKey="count"
                nameKey="status"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label
              >
                {developerData.client_pipeline.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Client Sources */}
      {developerData?.client_sources && developerData.client_sources.length > 0 && (
        <div className="card">
          <h2 className="card-title">Client Sources</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Count</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {developerData.client_sources.map((source, idx) => {
                  const total = developerData.client_sources.reduce((sum, s) => sum + (s.count || 0), 0);
                  const percentage = total > 0 ? ((source.count / total) * 100).toFixed(1) : 0;
                  return (
                    <tr key={idx}>
                      <td>{source.source || 'Unknown'}</td>
                      <td>{source.count || 0}</td>
                      <td>{percentage}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ethnicity Distribution */}
      {ethnicityStats && ethnicityStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="card-title" style={{ marginBottom: 0 }}>Client Ethnicity Distribution</h2>
              <Link
                to="/vdnb"
                className="btn btn-primary"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                View VDNB →
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ethnicityStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {ethnicityStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h2 className="card-title">Ethnicity Distribution (Bar Chart)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ethnicityStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="ethnicity" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#2563eb" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Property Selling Statistics */}
      {propertySellingStats && propertySellingStats.total > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 className="card-title" style={{ marginBottom: 0 }}>Properties Available for Sale</h2>
            <Link
              to="/vdnb"
              className="btn btn-primary"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              View All Properties →
            </Link>
          </div>

          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '20px' }}>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Total Properties</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{propertySellingStats.total}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Total Value</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2rem' }}>₹{formatCurrency(propertySellingStats.totalValue)}</div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Avg Value</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2rem' }}>
                ₹{formatCurrency(propertySellingStats.total > 0 ? propertySellingStats.totalValue / propertySellingStats.total : 0)}
              </div>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
              <div className="stat-label" style={{ color: '#fff' }}>Property Types</div>
              <div className="stat-value" style={{ color: '#fff', fontSize: '2.5rem' }}>{Object.keys(propertySellingStats.byType).length}</div>
            </div>
          </div>

          {Object.keys(propertySellingStats.byType).length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Properties by Type</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Property Type</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(propertySellingStats.byType).map(([type, count], idx) => {
                      const percentage = propertySellingStats.total > 0 ? ((count / propertySellingStats.total) * 100).toFixed(1) : 0;
                      return (
                        <tr key={idx}>
                          <td>{type}</td>
                          <td>{count}</td>
                          <td>{percentage}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {Object.keys(propertySellingStats.byBHK).length > 0 && (
            <div>
              <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Properties by BHK</h3>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>BHK Type</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(propertySellingStats.byBHK).map(([bhk, count], idx) => {
                      const percentage = propertySellingStats.total > 0 ? ((count / propertySellingStats.total) * 100).toFixed(1) : 0;
                      return (
                        <tr key={idx}>
                          <td>{bhk}</td>
                          <td>{count}</td>
                          <td>{percentage}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Brokers Matrix */}
      {developerData?.active_brokers && developerData.active_brokers.length > 0 && (
        <div className="card">
          <h2 className="card-title">Active Brokers Matrix</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Broker Name</th>
                  <th>Total Deals</th>
                  <th>Commission Rate</th>
                  <th>Active Clients</th>
                  <th>Total Revenue</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {developerData.active_brokers.map((broker, idx) => (
                  <tr key={idx}>
                    <td>{broker.name}</td>
                    <td>{broker.total_sales || 0}</td>
                    <td>{broker.commission_rate || 2}%</td>
                    <td>{broker.active_clients || 0}</td>
                    <td>₹{formatCurrency(broker.total_revenue || 0)}</td>
                    <td><span className="badge badge-success">Active</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Channel Partner Performance */}
      {developerData?.channel_partner_performance && developerData.channel_partner_performance.length > 0 && (
        <div className="card">
          <h2 className="card-title">Channel Partner Performance</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Partner Name</th>
                  <th>Deals</th>
                  <th>Revenue</th>
                  <th>Commission</th>
                </tr>
              </thead>
              <tbody>
                {developerData.channel_partner_performance.map((cp, idx) => (
                  <tr key={idx}>
                    <td>{cp.name}</td>
                    <td>{cp.deals || 0}</td>
                    <td>₹{formatCurrency(cp.revenue || 0)}</td>
                    <td>₹{formatCurrency(cp.commission || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Direct vs Broker Sales Comparison */}
      <div className="card">
        <h2 className="card-title">Direct Sales vs Broker Sales</h2>
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="stat-card">
            <div className="stat-label">Direct SIR Sales</div>
            <div className="stat-value">{developerData?.sales_overview?.direct_sir_sales || 0}</div>
            <div className="stat-label">
              Commission: ₹{formatCurrency(developerData?.commission_overview?.total_sir_commission || 0)}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Broker Sales</div>
            <div className="stat-value">{developerData?.sales_overview?.channel_sales || 0}</div>
            <div className="stat-label">
              Commission: ₹{formatCurrency(developerData?.commission_overview?.total_channel_commission || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status */}
      {developerData?.payment_status && developerData.payment_status.length > 0 && (
        <div className="card">
          <h2 className="card-title">Commission Payment Status</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Count</th>
                  <th>Total Amount</th>
                </tr>
              </thead>
              <tbody>
                {developerData.payment_status.map((payment, idx) => (
                  <tr key={idx}>
                    <td>
                      <span className={`badge badge-${payment.status === 'paid' ? 'success' : payment.status === 'pending' ? 'warning' : 'info'}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td>{payment.count || 0}</td>
                    <td>₹{formatCurrency(payment.total_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Media Campaigns */}
      {developerData?.media_campaigns && developerData.media_campaigns.length > 0 && (
        <div className="card">
          <h2 className="card-title">Media Campaigns Summary</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Campaigns</th>
                  <th>Total Budget</th>
                  <th>Total Spent</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {developerData.media_campaigns.map((campaign, idx) => (
                  <tr key={idx}>
                    <td>{campaign.type}</td>
                    <td>{campaign.category || 'N/A'}</td>
                    <td>{campaign.campaign_count || 0}</td>
                    <td>₹{formatCurrency(campaign.total_budget || 0)}</td>
                    <td>₹{formatCurrency(campaign.total_spent || 0)}</td>
                    <td>₹{formatCurrency((campaign.total_budget || 0) - (campaign.total_spent || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Projects Summary */}
      {developerData?.projects && developerData.projects.length > 0 && (
        <div className="card">
          <h2 className="card-title">Projects Summary</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Project Name</th>
                  <th>Total Units</th>
                  <th>Units Sold</th>
                  <th>Available</th>
                  <th>Revenue</th>
                  <th>Sales %</th>
                </tr>
              </thead>
              <tbody>
                {developerData.projects.map((project, idx) => {
                  const sold = project.units_sold || 0;
                  const total = project.total_units || 1;
                  const available = total - sold;
                  const salesPercent = ((sold / total) * 100).toFixed(1);
                  return (
                    <tr key={idx}>
                      <td>{project.name}</td>
                      <td>{total}</td>
                      <td>{sold}</td>
                      <td>{available}</td>
                      <td>₹{formatCurrency(project.revenue || 0)}</td>
                      <td>{salesPercent}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Sales */}
      {developerData?.recent_sales && developerData.recent_sales.length > 0 && (
        <div className="card">
          <h2 className="card-title">Recent Sales</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Deal Number</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Sale Value</th>
                  <th>Type</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {developerData.recent_sales.map((sale, idx) => (
                  <tr key={idx}>
                    <td>{sale.deal_number}</td>
                    <td>{sale.client_name}</td>
                    <td>{sale.project_name}</td>
                    <td>₹{formatCurrency(sale.sale_value)}</td>
                    <td>
                      <span className={`badge ${sale.is_direct_sir_sale ? 'badge-success' : 'badge-info'}`}>
                        {sale.is_direct_sir_sale ? 'Direct SIR' : 'Channel Partner'}
                      </span>
                    </td>
                    <td>{new Date(sale.sale_date).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SIR Team Performance */}
      {sirData?.team_performance && sirData.team_performance.length > 0 && (
        <div className="card">
          <h2 className="card-title">SIR Team Performance</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Team Member</th>
                  <th>Clients Assigned</th>
                  <th>Sales Closed</th>
                  <th>Revenue Generated</th>
                </tr>
              </thead>
              <tbody>
                {sirData.team_performance.map((member, idx) => (
                  <tr key={idx}>
                    <td>{member.name}</td>
                    <td>{member.clients_assigned || 0}</td>
                    <td>{member.sales_closed || 0}</td>
                    <td>₹{formatCurrency(member.revenue_generated || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Today's Activities */}
      {sirData?.today_activities && (
        <div className="card">
          <h2 className="card-title">Today's Activities</h2>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="stat-card">
              <div className="stat-label">Walk-ins</div>
              <div className="stat-value">{sirData.today_activities.today_walkins || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Follow-ups</div>
              <div className="stat-value">{sirData.today_activities.today_followups || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Follow-ups */}
      {sirData?.pending_followups && sirData.pending_followups.length > 0 && (
        <div className="card">
          <h2 className="card-title">Pending Follow-ups</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Phone</th>
                  <th>Scheduled Date</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {sirData.pending_followups.slice(0, 10).map((followup, idx) => (
                  <tr key={idx}>
                    <td>{followup.client_name}</td>
                    <td>{followup.client_phone}</td>
                    <td>{new Date(followup.scheduled_date).toLocaleDateString()}</td>
                    <td>{followup.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Launch Strategy War Room Section */}
      {strategies.length > 0 && (
        <>
          <div className="card" style={{ marginBottom: '24px', border: '2px solid #2563eb', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 className="card-title" style={{ color: '#1e40af', marginBottom: '8px' }}>🚀 Project Launch War Room</h2>
                <p style={{ color: '#64748b', fontSize: '14px' }}>Active Launch Strategies & Activities - January 1, 2026 Launch</p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a href="/launch-war-room" className="btn btn-primary" style={{ textDecoration: 'none' }}>
                  View War Room →
                </a>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              {strategies.map((strategy, idx) => (
                <div key={idx} style={{
                  padding: '16px',
                  background: 'white',
                  borderRadius: '8px',
                  border: '1px solid #bfdbfe'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e40af', marginBottom: '4px' }}>
                        {strategy.strategy_name}
                      </h3>
                      <p style={{ fontSize: '12px', color: '#64748b' }}>
                        {strategy.project_name}
                      </p>
                    </div>
                    <span className="badge" style={{
                      backgroundColor: strategy.status === 'active' ? '#10b98120' : '#6b728020',
                      color: strategy.status === 'active' ? '#10b981' : '#6b7280'
                    }}>
                      {strategy.status}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                    <div>
                      <div style={{ color: '#64748b' }}>Target Sales</div>
                      <div style={{ fontWeight: '600', color: '#1e40af' }}>{strategy.target_sales || 0}</div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b' }}>Target Revenue</div>
                      <div style={{ fontWeight: '600', color: '#1e40af' }}>₹{formatCurrency(strategy.target_revenue || 0)}</div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b' }}>Activities</div>
                      <div style={{ fontWeight: '600' }}>{strategy.total_activities || 0}</div>
                    </div>
                    <div>
                      <div style={{ color: '#64748b' }}>Team Members</div>
                      <div style={{ fontWeight: '600' }}>{strategy.team_members || 0}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
                    {new Date(strategy.start_date).toLocaleDateString()} - {new Date(strategy.end_date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Activities */}
          {upcomingActivities.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">📅 Upcoming Activities (Next 7 Days)</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Date</th>
                      <th>Assigned To</th>
                      <th>Budget</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingActivities.map((activity, idx) => (
                      <tr key={idx}>
                        <td><strong>{activity.activity_name}</strong></td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: activity.activity_type === 'ATL' ? '#2563eb20' :
                              activity.activity_type === 'BTL' ? '#10b98120' :
                                activity.activity_type === 'Digital' ? '#f59e0b20' : '#6b728020',
                            color: activity.activity_type === 'ATL' ? '#2563eb' :
                              activity.activity_type === 'BTL' ? '#10b981' :
                                activity.activity_type === 'Digital' ? '#f59e0b' : '#6b7280'
                          }}>
                            {activity.activity_type}
                          </span>
                        </td>
                        <td>{activity.category}</td>
                        <td>{new Date(activity.start_date).toLocaleDateString()}</td>
                        <td>{activity.assigned_to_name || '-'}</td>
                        <td>₹{formatCurrency(activity.budget || 0)}</td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: activity.status === 'completed' ? '#10b98120' :
                              activity.status === 'in_progress' ? '#2563eb20' : '#6b728020',
                            color: activity.status === 'completed' ? '#10b981' :
                              activity.status === 'in_progress' ? '#2563eb' : '#6b7280'
                          }}>
                            {activity.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upcoming Meetings */}
          {upcomingMeetings.length > 0 && (
            <div className="card" style={{ marginBottom: '24px' }}>
              <h2 className="card-title">🤝 Upcoming Channel Partner Meetings (Next 7 Days)</h2>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Meeting Type</th>
                      <th>Channel Partner</th>
                      <th>Date & Time</th>
                      <th>Location</th>
                      <th>Agenda</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingMeetings.map((meeting, idx) => (
                      <tr key={idx}>
                        <td><strong>{meeting.meeting_type}</strong></td>
                        <td>{meeting.channel_partner_name || 'All Partners'}</td>
                        <td>{new Date(meeting.scheduled_date).toLocaleString()}</td>
                        <td>{meeting.location || '-'}</td>
                        <td>{meeting.agenda ? (meeting.agenda.length > 50 ? meeting.agenda.substring(0, 50) + '...' : meeting.agenda) : '-'}</td>
                        <td>
                          <span className="badge" style={{
                            backgroundColor: meeting.status === 'completed' ? '#10b98120' : '#2563eb20',
                            color: meeting.status === 'completed' ? '#10b981' : '#2563eb'
                          }}>
                            {meeting.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {whatsappIntimations && whatsappIntimations.length > 0 && (
        <div className="card" style={{ marginBottom: '24px', background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)', color: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h2 className="card-title" style={{ color: 'white', marginBottom: '8px' }}>💬 WhatsApp Client Intimations</h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>Recent client inquiries via WhatsApp</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <a href="/whatsapp-intimation" className="btn btn-primary" style={{ textDecoration: 'none', background: 'white', color: '#25D366' }}>
                Manage All →
              </a>
            </div>
          </div>

          <div className="table-container" style={{ background: 'white', borderRadius: '8px', padding: '16px' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Phone</th>
                  <th>Project</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {whatsappIntimations.slice(0, 5).map((intimation) => (
                  <tr key={intimation.id}>
                    <td><strong>{intimation.client_name}</strong></td>
                    <td>{intimation.client_phone}</td>
                    <td>{intimation.project_name || '-'}</td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: intimation.priority === 'high' ? '#ef444420' :
                          intimation.priority === 'medium' ? '#f59e0b20' : '#10b98120',
                        color: intimation.priority === 'high' ? '#ef4444' :
                          intimation.priority === 'medium' ? '#f59e0b' : '#10b981'
                      }}>
                        {intimation.priority}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{
                        backgroundColor: intimation.status === 'new' ? '#3b82f620' :
                          intimation.status === 'acknowledged' ? '#10b98120' : '#6b728020',
                        color: intimation.status === 'new' ? '#3b82f6' :
                          intimation.status === 'acknowledged' ? '#10b981' : '#6b7280'
                      }}>
                        {intimation.status}
                      </span>
                    </td>
                    <td>{new Date(intimation.intimation_time).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Quick Actions FAB */}
      {user && <QuickActions />}
    </div>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount);
};

export default Dashboard;
